import axiosInstance from '../../../axios';
import React, { useCallback, useState } from 'react';
import { getErrorMessage } from '../../../utils/request';

const CreatePageContext = React.createContext();

function CreatePageProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const createPage = useCallback(async (packageId, payload) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `/packages/${packageId}/pages`,
        payload,
      );

      return {
        page: getPackageFromResponse(response),
        success: true,
        error: null,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrorMessage(errorMessage);
      return { success: false, errorMessage };
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  const value = {
    loading,
    createPage,
    hasLoaded,
    errorMessage,
  };

  return (
    <CreatePageContext.Provider value={value}>
      {children}
    </CreatePageContext.Provider>
  );
}

/**
 * @typedef {{
 *  id: string;
 *  numQuestions: number;
 *  type: string
 *  title: string
 *  image: string
 *  header: string
 *  questionOrder: string
 *  showInstantFeedback: boolean;
 *  showResultsAnswers: boolean;
 *  backgroundColor: string
 *  backgroundImage: string
 *  restartMode: string
 *  testDuration: string
 *  tallyMode: string
 * }} Page
 */

/**
 * @returns {{
 *  loading: boolean,
 *  createPage: (page: Page) => Promise<{ page: Page; success: boolean; errorMessage: string; }>
 *  hasLoaded: boolean,
 *  errorMessage: string
 * }}
 */

function useCreatePage() {
  const context = React.useContext(CreatePageContext);

  if (context === undefined) {
    throw new Error('useCreatePage must be used within a CreatePageProvider');
  }

  return context;
}

function getPackageFromResponse(response) {
  if (!response.data) {
    return null;
  }

  const pkg = response.data;
  return {
    id: pkg.id,
    name: pkg.name,
  };
}

export { CreatePageProvider, useCreatePage };
